'use client';

import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { nb } from '@/lib/nobreak';

/**
 * LEAK FINDER, "the leaky pipeline" (rebuilt 2026-10-02 after the owner rated the card-grid version a C-).
 *
 * The story in one picture: borrowers you paid to attract stream into a pipe (the same particles as the Magnet hero)
 * and drip out at eight stations in funnel order; the tank at the end, "your pipeline", barely fills. Tap a station and
 * it tells its own story in one panel (what it looks like, what it costs, the fix). Mark the ones that sound like you,
 * then "Build my system": the foundation (Mortgage Sales Funnels) turns the dashed outline into a solid pipe, each part
 * snaps in and seals its leaks (Convert AI seals four at once), the flow reaches the tank, and you leave with your build
 * in install order and the call.
 *
 * Crawlable, one DOM for every screen size: stations (buttons) and their panels are interleaved siblings in the
 * server HTML. On desktop a CSS grid lays the stations along one row and shows the open panel underneath; on phones
 * the pipe runs down the left and each panel opens under its station. Every leak, panel and part is real text
 * (accordion pattern, the panels that are not open carry the hidden attribute). Claims live in the page: every sentence
 * is a prop. The particles are illustrative and the page says so; no counts, no prices, no score of their business.
 * The CTA carries ids only (ref=leak-finder&leaks=…&build=…); the form keeps the query on every step.
 *
 * Motion: canvas inside the component, runs only while the track is on screen and the tab is visible, capped particles
 * and pixels, 30 fps on phones. Reduced motion: no canvas; leaking stations show still drips (CSS) and the build applies
 * at once. Styles live in the game skin (skin-game-css.ts, "LEAK FINDER").
 */
export type LeakPart = { id: string; name: string; role: 'foundation' | 'accelerator'; benefit: string; points: string[] };
export type Leak = {
  id: string; area: string; name: string; symptom: string;
  /** the station label on the pipe (one or two words); the panel headline stays the full name */
  short?: string;
  looks: string; costs: string; fix: string;
  /** part ids that plug this leak */
  plugs: string[];
  /** part ids that help with it (shown, not counted) */
  helps?: string[];
};

const DESKTOP = '(min-width: 1024px)';
const pad2 = (n: number) => String(n).padStart(2, '0');

export function LPLeakFinder({ leaks, parts, href, button, note }: { leaks: Leak[]; parts: LeakPart[]; href: string; button: string; note?: string }) {
  const uid = useId().replace(/:/g, '');
  const [open, setOpen] = useState<string | null>(null);
  // the "how it works" overlay: shown until the visitor starts (remembered for this tab)
  const [intro, setIntro] = useState(true);
  const [touched, setTouched] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const [mine, setMine] = useState<string[]>([]);
  const [built, setBuilt] = useState<string[]>([]);
  const [phase, setPhase] = useState<'idle' | 'building' | 'done'>('idle');
  const [caption, setCaption] = useState('');
  const timers = useRef<number[]>([]);
  const rootRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tankRef = useRef<HTMLDivElement>(null);

  const foundation = parts.find((p) => p.role === 'foundation');
  const partOf = (id: string) => parts.find((p) => p.id === id);
  const plugged = useCallback((l: Leak) => l.plugs.some((p) => built.includes(p)), [built]);
  const leaking = useMemo(() => leaks.map((l) => !plugged(l)), [leaks, plugged]);
  const pluggedCount = leaks.length - leaking.filter(Boolean).length;
  const hasFoundation = !!foundation && built.includes(foundation.id);

  useEffect(() => { try { if (sessionStorage.getItem('lp-lf-started') === '1') setIntro(false); } catch { /* ignore */ } }, []);
  // while the overlay is up, the stage behind it is not focusable
  useEffect(() => { const el = stageRef.current; if (!el) return; if (intro) el.setAttribute('inert', ''); else el.removeAttribute('inert'); }, [intro]);
  const start = () => {
    setIntro(false);
    try { sessionStorage.setItem('lp-lf-started', '1'); } catch { /* ignore */ }
    window.setTimeout(() => stageRef.current?.querySelector<HTMLButtonElement>('.lp-lf-st')?.focus({ preventScroll: true }), 60);
  };
  useEffect(() => () => timers.current.forEach((t) => window.clearTimeout(t)), []);

  const openLeak = (id: string) => {
    setTouched(true);
    if (window.matchMedia(DESKTOP).matches) setOpen(id);
    else setOpen((o) => (o === id ? null : id));
  };
  const go = (id: string) => {
    setOpen(id);
    // phones: keep the newly opened leak in view
    if (!window.matchMedia(DESKTOP).matches) window.setTimeout(() => document.getElementById(`${uid}-s-${id}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' }), 30);
  };
  const toBar = () => { setOpen(null); window.setTimeout(() => { barRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' }); barRef.current?.querySelector<HTMLButtonElement>('.lp-lf-build')?.focus({ preventScroll: true }); }, 40); };
  const toggleMine = (id: string) => setMine((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));

  // the parts a build needs: the foundation, then whatever plugs the marked leaks (all of them when nothing is marked)
  const plan = useMemo(() => {
    const scope = mine.length ? leaks.filter((l) => mine.includes(l.id)) : leaks;
    const want = new Set<string>(foundation ? [foundation.id] : []);
    scope.forEach((l) => l.plugs.forEach((p) => want.add(p)));
    return parts.filter((p) => want.has(p.id)).map((p) => p.id);
  }, [mine, leaks, parts, foundation]);

  const sealsOf = (partId: string) => leaks.filter((l) => l.plugs.includes(partId));

  const build = () => {
    timers.current.forEach((t) => window.clearTimeout(t)); timers.current = [];
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const desktop = window.matchMedia(DESKTOP).matches;
    // fold the story panel so the build plays out right under the pipe (on phones the whole pipe then fits one screen),
    // then bring the pipe into view: folding the panel above the button makes the browser scroll the page up to keep the
    // button still (scroll anchoring), which would push the pipe off screen and hide the build
    setOpen(null);
    window.setTimeout(() => {
      const t = rootRef.current; if (!t) return;
      const y = t.getBoundingClientRect().top + window.scrollY - (desktop ? 96 : 84);
      window.scrollTo({ top: y, behavior: reduce ? 'auto' : 'smooth' });
    }, 40);
    if (reduce) { setBuilt(plan); setPhase('done'); setCaption(''); return; }
    setBuilt([]); setPhase('building');
    let t = desktop ? 150 : 650;
    plan.forEach((id, i) => {
      const p = partOf(id); if (!p) return;
      const n = sealsOf(id).length;
      const line = p.role === 'foundation'
        ? `${p.name} goes in first. It is the pipe itself.`
        : `${p.name} seals ${n === 1 ? 'a leak' : `${n} leaks at once`}.`;
      timers.current.push(window.setTimeout(() => { setBuilt((b) => (b.includes(id) ? b : [...b, id])); setCaption(line); }, t));
      t += i === 0 ? 1100 : 850;
    });
    timers.current.push(window.setTimeout(() => { setPhase('done'); setCaption(''); }, t + 300));
  };
  const addPart = (id: string) => setBuilt((b) => (b.includes(id) ? b : [...b, id]));
  const reset = () => {
    timers.current.forEach((x) => window.clearTimeout(x)); timers.current = [];
    setBuilt([]); setMine([]); setPhase('idle'); setCaption(''); setOpen(null);
  };

  // ---- the particle flow -------------------------------------------------------------------------------------------
  const leakingRef = useRef(leaking); leakingRef.current = leaking;
  const foundRef = useRef(hasFoundation); foundRef.current = hasFoundation;
  const prevLeaking = useRef(leaking);
  const sealRef = useRef<number[]>([]);
  useEffect(() => {
    leaking.forEach((on, i) => { if (prevLeaking.current[i] && !on) sealRef.current.push(i); });
    prevLeaking.current = leaking;
  }, [leaking]);

  useEffect(() => {
    const root = rootRef.current, track = trackRef.current, cv = canvasRef.current, tank = tankRef.current;
    if (!root || !track || !cv || !tank) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const ctx = cv.getContext('2d'); if (!ctx) return;
    root.setAttribute('data-anim', '');

    type P = { s: number; off: number; v: number; size: number; col: string; i: number };
    type D = { x: number; y: number; vx: number; vy: number; life: number; size: number };
    type R = { x: number; y: number; t: number };
    let W = 0, H = 0, axis: 'x' | 'y' = 'x', a0 = 0, a1 = 0, cross = 0, st: number[] = [], phone = false;
    let dark = true;
    const ps: P[] = [], ds: D[] = [], rings: R[] = [];
    let fill = 0.12, spawnAcc = 0, arrived = 0, spawned = 0, statAcc = 0, themeAcc = 0, raf = 0, last = 0, running = false, skip = false;

    const layout = () => {
      const r = track.getBoundingClientRect();
      W = r.width; H = r.height; phone = !window.matchMedia(DESKTOP).matches;
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5, Math.sqrt(2_000_000 / Math.max(1, W * H)));
      cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
      cv.style.width = `${W}px`; cv.style.height = `${H}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const c = (el: Element) => { const b = el.getBoundingClientRect(); return [b.left - r.left + b.width / 2, b.top - r.top + b.height / 2]; };
      const nodes = [...track.querySelectorAll('.lp-lf-node')].map(c);
      const vessel = track.querySelector('.lp-lf-vessel'); if (!nodes.length || !vessel) return;
      const tk = c(vessel);
      axis = phone ? 'y' : 'x';
      if (axis === 'x') { cross = nodes[0][1]; st = nodes.map((p) => p[0]); a0 = Math.max(0, st[0] - 70); a1 = tk[0]; }
      else { cross = nodes[0][0]; st = nodes.map((p) => p[1]); a0 = Math.max(0, st[0] - 40); a1 = tk[1]; }
      dark = document.documentElement.getAttribute('data-skin-mode') !== 'light';
    };
    const pal = () => dark ? ['34,228,255', '124,92,255', '255,124,198', '182,255,92'] : ['8,120,150', '91,61,245', '200,17,127'];
    const red = () => (dark ? '255,77,109' : '225,29,72');
    const pt = (s: number, off: number): [number, number] => (axis === 'x' ? [s, cross + off] : [cross + off, s]);

    const step = (dt: number) => {
      const rate = phone ? 22 : 42;
      spawnAcc += dt * rate;
      while (spawnAcc >= 1) {
        spawnAcc -= 1;
        if (ps.length < (phone ? 70 : 150)) {
          const p = pal();
          ps.push({ s: a0, off: (Math.random() - 0.5) * (phone ? 4 : 6), v: (a1 - a0) / (phone ? 2 : 2.3) * (0.85 + Math.random() * 0.3),
            size: (phone ? 1.3 : 1.6) + Math.random() * 1.5, col: p[Math.floor(Math.random() * p.length)], i: 0 });
          spawned++;
        }
      }
      const leak = leakingRef.current; const pLeak = foundRef.current ? 0.3 : 0.36;
      for (let k = ps.length - 1; k >= 0; k--) {
        const p = ps[k]; p.s += p.v * dt;
        let gone = false;
        while (p.i < st.length && p.s >= st[p.i]) {
          if (leak[p.i] && Math.random() < pLeak) {
            const [x, y] = pt(st[p.i], p.off);
            ds.push(axis === 'x'
              ? { x: x + (Math.random() - 0.5) * 8, y: y + 6, vx: (Math.random() - 0.5) * 30, vy: 25 + Math.random() * 40, life: 1, size: p.size + 0.9 }
              : { x: x - 6, y: y + (Math.random() - 0.5) * 6, vx: -(26 + Math.random() * 40), vy: Math.random() * 20, life: 1, size: p.size + 0.6 });
            gone = true; break;
          }
          p.i++;
        }
        if (gone) { ps.splice(k, 1); continue; }
        if (p.s >= a1) { ps.splice(k, 1); arrived++; }
      }
      for (let k = ds.length - 1; k >= 0; k--) {
        const d = ds[k]; d.vy += 420 * dt; d.x += d.vx * dt; d.y += d.vy * dt; d.life -= dt * (axis === 'x' ? 1.25 : 1.6);
        if (d.life <= 0) ds.splice(k, 1);
      }
      while (sealRef.current.length) { const i = sealRef.current.shift()!; if (st[i] != null) { const [x, y] = pt(st[i], 0); rings.push({ x, y, t: 0 }); } }
      for (let k = rings.length - 1; k >= 0; k--) { rings[k].t += dt; if (rings[k].t > 0.9) rings.splice(k, 1); }
      // the tank fills with the share of borrowers that make it through (illustrative), smoothed
      statAcc += dt;
      if (statAcc > 0.35) {
        const ratio = spawned ? arrived / spawned : 0; arrived = 0; spawned = 0; statAcc = 0;
        const target = 0.08 + ratio * 0.9; fill += (target - fill) * (target > fill ? 0.55 : 0.3);
        tank.style.setProperty('--fill', fill.toFixed(3));
      }
      themeAcc += dt; if (themeAcc > 1) { themeAcc = 0; dark = document.documentElement.getAttribute('data-skin-mode') !== 'light'; }
    };

    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      ctx.globalCompositeOperation = dark ? 'lighter' : 'source-over';
      for (const p of ps) {
        const [x, y] = pt(p.s, p.off); const [tx, ty] = pt(p.s - (phone ? 10 : 16), p.off);
        const g = ctx.createLinearGradient(tx, ty, x, y); g.addColorStop(0, `rgba(${p.col},0)`); g.addColorStop(1, `rgba(${p.col},.85)`);
        ctx.strokeStyle = g; ctx.lineWidth = p.size; ctx.lineCap = 'round';
        ctx.beginPath(); ctx.moveTo(tx, ty); ctx.lineTo(x, y); ctx.stroke();
        ctx.fillStyle = `rgba(${p.col},.95)`; ctx.beginPath(); ctx.arc(x, y, p.size * 0.9, 0, 6.283); ctx.fill();
      }
      const rc = red();
      for (const d of ds) {
        const a = Math.max(0, d.life);
        ctx.strokeStyle = `rgba(${rc},${a * 0.5})`; ctx.lineWidth = d.size * 1.1; ctx.lineCap = 'round';
        ctx.beginPath(); ctx.moveTo(d.x - d.vx * 0.05, d.y - d.vy * 0.05); ctx.lineTo(d.x, d.y); ctx.stroke();
        ctx.fillStyle = `rgba(${rc},${a * 0.95})`;
        ctx.beginPath(); ctx.arc(d.x, d.y, d.size, 0, 6.283); ctx.fill();
      }
      for (const r of rings) {
        const k = r.t / 0.9; ctx.strokeStyle = dark ? `rgba(182,255,92,${(1 - k) * 0.9})` : `rgba(18,161,80,${(1 - k) * 0.9})`;
        ctx.lineWidth = 2.5 * (1 - k) + 0.5; ctx.beginPath(); ctx.arc(r.x, r.y, 14 + k * 34, 0, 6.283); ctx.stroke();
      }
      ctx.globalCompositeOperation = 'source-over';
    };

    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      if (phone) { skip = !skip; if (skip) return; }
      const dt = Math.min(0.05, last ? (now - last) / 1000 : 0.016); last = now;
      step(dt); draw();
    };
    const start = () => { if (running || document.hidden) return; running = true; last = 0; raf = requestAnimationFrame(frame); };
    const stop = () => { running = false; cancelAnimationFrame(raf); };

    layout();
    // warm up so the first visible frame already shows a flowing, leaking pipe
    for (let k = 0; k < 90; k++) step(1 / 30);
    let inView = false;
    const io = new IntersectionObserver(([e]) => { inView = e.isIntersecting; if (inView) start(); else stop(); }, { rootMargin: '120px 0px' });
    io.observe(track);
    let first = true; // the observer fires once on observe: keep the warmed-up flow
    const ro = new ResizeObserver(() => { if (first) { first = false; return; } layout(); ps.length = 0; ds.length = 0; });
    ro.observe(track);
    const vis = () => { if (document.hidden) stop(); else if (inView) start(); };
    document.addEventListener('visibilitychange', vis);
    return () => { stop(); io.disconnect(); ro.disconnect(); document.removeEventListener('visibilitychange', vis); root.removeAttribute('data-anim'); };
  }, []);

  // ---- render -------------------------------------------------------------------------------------------------------
  const q = new URLSearchParams({ ref: 'leak-finder' });
  if (mine.length) q.set('leaks', mine.join(','));
  if (built.length) q.set('build', built.join(','));
  const ctaHref = `${href}${href.includes('?') ? '&' : '?'}${q.toString()}`;
  const stillLeaking = leaks.filter((l) => !plugged(l));
  const step = phase === 'done' ? 3 : phase === 'building' || mine.length > 0 ? 2 : 1;
  const label = (l: Leak) => l.short || l.name;
  const mineNames = leaks.filter((l) => mine.includes(l.id)).map(label);

  return (
    <div ref={rootRef} className="lp-lf" data-found={hasFoundation ? '' : undefined} data-phase={phase} data-intro={intro ? '' : undefined}
      data-hint={!intro && !touched && phase === 'idle' ? '' : undefined}>
      <div ref={stageRef} className="lp-lf-stage">
        <div className="lp-lf-steps">
          <ol>
            {['Find your leaks', 'Plug them', 'Your build'].map((t, i) => (
              <li key={t} data-on={step === i + 1 ? '' : undefined} data-past={step > i + 1 ? '' : undefined}><span className="lp-num">{i + 1}</span>{t}</li>
            ))}
          </ol>
          <button type="button" className="lp-lf-howto" onClick={() => setIntro(true)}>How it works</button>
        </div>
        <p className="lp-lf-tip">Tap a red station to see what that leak costs you.</p>

        <div ref={trackRef} className="lp-lf-track">
          <span className="lp-lf-pipe" aria-hidden />
          <canvas ref={canvasRef} className="lp-lf-canvas" aria-hidden />
          {leaks.map((l, i) => {
            const isOpen = open === l.id;
            const isPlugged = !leaking[i];
            const isMine = mine.includes(l.id);
            const fixer = l.plugs.map((p) => partOf(p)).filter(Boolean) as LeakPart[];
            const others = Array.from(new Set(l.plugs.flatMap((p) => sealsOf(p).map((x) => x.id)))).filter((id) => id !== l.id);
            const next = leaks[i + 1];
            const prev = leaks[i - 1];
            return [
              <button key={`s-${l.id}`} type="button" id={`${uid}-s-${l.id}`} className="lp-lf-st" aria-expanded={isOpen} aria-controls={`${uid}-p-${l.id}`}
                onClick={() => openLeak(l.id)} data-state={isPlugged ? 'plugged' : 'leaking'} data-mine={isMine ? '' : undefined}
                style={{ ['--i' as string]: i }}>
                <span className="lp-lf-name">{label(l)}</span>
                <span key={isPlugged ? 'p' : 'l'} className="lp-lf-node lp-num" aria-hidden>
                  {isPlugged ? <svg viewBox="0 0 20 20" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.6"><path d="M4.5 10.5l3.5 3.5 7.5-8" strokeLinecap="round" strokeLinejoin="round" /></svg> : pad2(i + 1)}
                </span>
                <span className="lp-lf-sym">{nb(l.symptom)}</span>
                <span className="sr-only">{isPlugged ? ' (plugged)' : ' (leaking)'}{isMine ? ', marked as yours' : ''}</span>
              </button>,
              <div key={`p-${l.id}`} id={`${uid}-p-${l.id}`} role="region" aria-labelledby={`${uid}-s-${l.id}`} className="lp-lf-panel" hidden={!isOpen}
                style={{ ['--lf-i' as string]: i }} data-state={isPlugged ? 'plugged' : 'leaking'}>
                <div className="lp-lf-story">
                  <p className="lp-lf-eyebrow">Leak {i + 1} of {leaks.length} · {l.area}</p>
                  <h3 className="lp-lf-title">{l.name}</h3>
                  <p className="lp-lf-symptom">{nb(l.symptom)}</p>
                  <dl className="lp-lf-facts">
                    <div><dt>What it looks like</dt><dd>{nb(l.looks)}</dd></div>
                    <div><dt>What it costs</dt><dd>{nb(l.costs)}</dd></div>
                  </dl>
                  <button type="button" className="lp-lf-mine" aria-pressed={isMine} onClick={() => toggleMine(l.id)}>
                    <span aria-hidden className="lp-lf-mine-box">{isMine && <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="3"><path d="M4.5 10.5l3.5 3.5 7.5-8" strokeLinecap="round" strokeLinejoin="round" /></svg>}</span>
                    {isMine ? 'Marked as one of my leaks' : 'This is one of my leaks'}
                  </button>
                </div>
                <div className="lp-lf-fix">
                  <p className="lp-lf-eyebrow">The fix</p>
                  <p className="lp-lf-fixtext">{l.fix}</p>
                  {fixer.map((p) => (
                    <div key={p.id} className="lp-lf-part" data-on={built.includes(p.id) ? '' : undefined}>
                      <p className="lp-lf-part-state">{built.includes(p.id) ? 'Plugged by' : 'Plugged with'}</p>
                      <p className="lp-lf-part-name">{p.name}</p>
                      <p className="lp-lf-part-benefit">{nb(p.benefit)}</p>
                    </div>
                  ))}
                  {l.helps?.length ? <p className="lp-lf-helps">Helped by {l.helps.map((h) => partOf(h)?.name).filter(Boolean).join(' and ')}.</p> : null}
                  {others.length > 0 && (
                    <div className="lp-lf-also">
                      <p>The same part also plugs</p>
                      <div>{others.map((id) => { const o = leaks.find((x) => x.id === id)!; return <button key={id} type="button" onClick={() => go(id)}>{label(o)}</button>; })}</div>
                    </div>
                  )}
                </div>
                <div className="lp-lf-nav">
                  {prev ? <button type="button" className="lp-lf-prev" onClick={() => go(prev.id)}>← {label(prev)}</button> : <span />}
                  {next
                    ? <button type="button" className="lp-lf-next" onClick={() => go(next.id)}>Next leak: {label(next)} →</button>
                    : <button type="button" className="lp-lf-next" onClick={toBar}>Next: plug your leaks →</button>}
                </div>
              </div>,
            ];
          })}
          <div className="lp-lf-empty" hidden={open !== null || phase !== 'idle'} aria-hidden>
            <span className="lp-lf-empty-arrow">↑</span>
            <p>Tap a red station to see what that leak costs you.</p>
          </div>
          <div className="lp-lf-tank">
            <span className="lp-lf-tank-label">Your pipeline</span>
            <div ref={tankRef} className="lp-lf-vessel" aria-hidden><span className="lp-lf-fill" /></div>
          </div>
        </div>

        <div ref={barRef} className="lp-lf-bar" aria-live="polite">
          {phase === 'idle' && (
            <>
              <div className="lp-lf-barcopy">
                <p className="lp-lf-eyebrow">Step 2 · Plug your leaks</p>
                <p className="lp-lf-hint">
                  {mine.length === 0
                    ? `Mark the leaks that sound like you, or plug all ${leaks.length} at once.`
                    : <>Your leaks: {mineNames.map((n) => <span key={n} className="lp-lf-chip">{n}</span>)}</>}
                </p>
              </div>
              <button type="button" className="lp-lf-build" onClick={build}>
                {mine.length ? `Plug my ${mine.length} ${mine.length === 1 ? 'leak' : 'leaks'}` : `Plug all ${leaks.length} leaks`}
                <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden><path d="M4 10h11M11 5l5 5-5 5" strokeLinecap="round" /></svg>
              </button>
            </>
          )}
          {phase === 'building' && <p className="lp-lf-caption">{caption}</p>}
          {phase === 'done' && (
            <div className="lp-lf-done">
              <div>
                <p className="lp-lf-eyebrow">Step 3 · Your build</p>
                <ol className="lp-lf-buildlist">
                  {parts.filter((p) => built.includes(p.id)).map((p, i) => <li key={p.id}><span className="lp-num">{i + 1}</span>{p.name}</li>)}
                </ol>
                <p className="lp-lf-count"><span className="lp-num">{pluggedCount}</span> of {leaks.length} leaks plugged</p>
                {stillLeaking.length > 0 && (
                  <div className="lp-lf-still">
                    <p>Still leaking:</p>
                    {stillLeaking.map((l) => { const p = partOf(l.plugs[0]); return p ? <button key={l.id} type="button" onClick={() => addPart(p.id)}>{label(l)} <span>add {p.name}</span></button> : null; })}
                  </div>
                )}
              </div>
              <div className="lp-lf-cta">
                <a href={ctaHref} className="lp-shimmer inline-flex h-14 w-full items-center justify-center gap-2.5 rounded-full bg-[#ff34a7] px-6 text-center text-[1rem] font-semibold text-white">
                  {button}
                  <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden><path d="M4 10h11M11 5l5 5-5 5" strokeLinecap="round" /></svg>
                </a>
                {note && <p className="lp-lf-note">{note}</p>}
                <button type="button" className="lp-lf-reset" onClick={reset}>Start over</button>
              </div>
            </div>
          )}
        </div>
      </div>

      {intro && (
        <div className="lp-lf-ov" role="group" aria-label="How the Leak Finder works">
          <div className="lp-lf-ov-card">
            <p className="lp-lf-eyebrow">How it works</p>
            <p className="lp-lf-ov-title">Find where your marketing leaks borrowers.</p>
            <ol className="lp-lf-ov-steps">
              <li><span className="lp-lf-ov-icon" data-k="drip" aria-hidden /><p><strong>Watch the pipe.</strong> The red drips are borrowers you paid to attract and then lost.</p></li>
              <li><span className="lp-lf-ov-icon" data-k="tap" aria-hidden /><p><strong>Tap a red station</strong> to see what that leak costs you. Mark the ones that sound like your business.</p></li>
              <li><span className="lp-lf-ov-icon" data-k="plug" aria-hidden /><p><strong>Plug your leaks</strong> and watch your pipeline fill. You leave with your build, in the order to put it in.</p></li>
            </ol>
            <button type="button" className="lp-lf-start" onClick={start}>
              Find my leaks
              <svg viewBox="0 0 20 20" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden><path d="M4 10h11M11 5l5 5-5 5" strokeLinecap="round" /></svg>
            </button>
          </div>
        </div>
      )}

      <details className="lp-lf-parts">
        <summary>The {parts.length} parts of the system</summary>
        <ol>
          {parts.map((p) => (
            <li key={p.id} data-on={built.includes(p.id) ? '' : undefined}>
              <p className="lp-lf-part-name">{p.name}{p.role === 'foundation' && <> <span className="lp-lf-tag">Foundation</span></>}</p>
              <p className="lp-lf-part-benefit">{nb(p.benefit)}</p>
              <ul>{p.points.map((pt) => <li key={pt}>{nb(pt)}</li>)}</ul>
            </li>
          ))}
        </ol>
      </details>
    </div>
  );
}
